/**
 * LeftMenu — persistent vertical icon bar on the left edge.
 *
 * The design library version is a presentational component only; app
 * navigation and data loading are supplied by the host.
 *
 * Accessibility: Each button has proper aria-label and the active item
 * is marked with aria-current="page" for screen reader users.
 */

import React, { useEffect, useRef } from 'react';
import { Tooltip } from 'primereact/tooltip';

export type LeftMenuIconMode = 'material-symbol' | 'sprite' | 'image';

export interface LeftMenuButton {
  id: string;
  icon: string;
  iconMode?: LeftMenuIconMode;
  iconClassName?: string;
  iconFallback?: string;
  label: string;
  active?: boolean;
  showBlueDot?: boolean;
  showRedDot?: boolean;
  homePage?: string;
  tourId?: string;
}

interface LeftMenuProps {
  buttons: LeftMenuButton[];
  onButtonClick: (button: LeftMenuButton) => void;
  //onButtonHover?: (button: LeftMenuButton, triggerEl: HTMLElement) => void;
  onStartFeatureTour?: () => Promise<void> | void;
}

function renderIcon(button: LeftMenuButton): React.ReactNode {
  const iconClassName = button.iconClassName?.trim();
  const materialFallback = button.iconFallback?.trim();

  if (button.iconMode === 'sprite') {
    if (button.icon.trim().length === 0 && materialFallback) {

      return (
        <span
          className={['material-symbols-outlined', iconClassName].filter(Boolean).join(' ')}
          aria-hidden="true"
        >
          {materialFallback}
        </span>
      );
    }

    return (
      <svg
        className={iconClassName}
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 24 24"
      >
        <use href={button.icon} />
      </svg>
    );
  }

  if (button.iconMode === 'image') {
    if (materialFallback) {
      return (
        <span
          className={['material-symbols-outlined', iconClassName].filter(Boolean).join(' ')}
          aria-hidden="true"
        >
          {materialFallback}
        </span>
      );
    }

    return (
      <img
        src={button.icon}
        alt=""
        className={iconClassName}
        aria-hidden="true"
      />
    );
  }

  return (
    <span
      className={['material-symbols-outlined', iconClassName].filter(Boolean).join(' ')}
      aria-hidden="true"
    >
      {button.icon}
    </span>
  );
}

export const LeftMenu = React.memo(function LeftMenu({ buttons, onButtonClick, onStartFeatureTour }: LeftMenuProps) {

  const hasStartedTourRef = useRef(false);

  useEffect(() => {
    if (!onStartFeatureTour || hasStartedTourRef.current || buttons.length === 0) {
      return;
    }

    hasStartedTourRef.current = true;
    void onStartFeatureTour();
  }, [buttons.length, onStartFeatureTour]);

  return (
    <nav className="app-dock" id="leftMenu" aria-label="App switcher">
      {/* Mount only after buttons exist so PrimeReact scans a populated DOM */}
      {buttons.length > 0 && (
        <Tooltip target="#leftMenu .app-dock__btn" position="right" className="app-dock__tooltip" />
      )}
      {buttons.map((btn, idx) => (
        <button
          key={`${btn.id}-${idx}`}
          className={`app-dock__btn${btn.active ? ' app-dock__btn--active' : ''}`}
          data-dock-app={btn.id}
          data-tour-id={btn.tourId}
          data-pr-tooltip={btn.label}
          data-pr-position="right"
          type="button"
          onClick={() => onButtonClick(btn)}
          aria-pressed={btn.active ?? false}
          aria-current={btn.active ? 'page' : undefined}
          aria-label={btn.label}
        >
          {renderIcon(btn)}
          {btn.showBlueDot && (
            <span
              className="app-dock__notification-dot app-dock__notification-dot--blue app-dock__notification-dot--visible"
              aria-hidden="true"
            />
          )}
          {btn.showRedDot && (
            <span
              className="app-dock__notification-dot app-dock__notification-dot--visible"
              aria-hidden="true"
            />
          )}
        </button>
      ))}
    </nav>
  );
});

export default LeftMenu;
